import type { IDepartureCategory, IResource } from '@bc-library/interface'
import { nextTick, ref } from 'vue'
import { sortByDate } from '@/services/application'
import { resourceApi } from '@/services/api'

// TODO: Update this function to match v4
export const getCurrentJob = (resource: IResource) => {
  const isCurrentJob = resource.jobs.find(job => !job.endDate)
  if (isCurrentJob)
    return isCurrentJob

  return resource.jobs[0] || null
}

type IResourceDeparture = Pick<IResource, 'jobs' | 'departureDate' | 'departureDatePhysical' | 'departureCategoryId'> & {
  departureCategory?: IDepartureCategory | null
}

/**
 * The job that ended on the resource departure date: the API keeps the departure reason on it
 */
export const getDepartureJob = (resource: IResourceDeparture) => {
  if (!resource?.departureDate) return null
  const departureDay = new Date(resource.departureDate).toDateString()
  return resource.jobs.find(job => !job.isAlumni && job.endDate && new Date(job.endDate).toDateString() === departureDay) || null
}

/**
 * Copies the resource departure reason onto its departure job, as the API does, so the jobs sent back
 * with the resource keep it (a rehire moves the reason from the resource to that job)
 */
export const keepDepartureReasonOnJob = (resource: IResourceDeparture) => {
  const departureJob = getDepartureJob(resource)
  const category = resource.departureCategoryId
  if (!departureJob || !category) return
  const categoryId = typeof category === 'object' ? category._id : category
  departureJob.alumni = {
    ...departureJob.alumni,
    departureDate: departureJob.alumni?.departureDate || departureJob.endDate,
    departureDatePhysical: departureJob.alumni?.departureDatePhysical || resource.departureDatePhysical || null,
    departureCategoryId: categoryId,
    departureCategory: typeof category === 'object'
      ? category
      : (resource.departureCategory?._id === categoryId ? resource.departureCategory : null),
  }
}

/**
 * The departure date, the alumni flag and the departure reason are synced from the jobs by the API:
 * reads them back from the saved resource
 */
export const applySyncedDeparture = (resource: IResourceDeparture & Pick<IResource, 'isActive'>, savedResource?: Partial<IResource> | null) => {
  resource.departureDate = savedResource?.departureDate || null
  resource.isActive = savedResource?.isActive ?? resource.isActive
  const category = savedResource?.departureCategoryId
  const categoryId = (typeof category === 'object' ? category?._id : category) || null
  resource.departureCategoryId = categoryId
  resource.departureCategory = typeof category === 'object' && category
    ? category
    : (categoryId && resource.departureCategory?._id === categoryId ? resource.departureCategory : null)
  keepDepartureReasonOnJob(resource)
}

export const isCurrentJobActive = (
  currentJob?: { startDate?: Date | string | null, endDate?: Date | string | null } | null,
  referenceDate: Date = new Date(),
) => {
  if (!currentJob?.startDate)
    return false

  const todayStart = new Date(referenceDate)
  todayStart.setHours(0, 0, 0, 0)

  const todayEnd = new Date(referenceDate)
  todayEnd.setHours(23, 59, 59, 999)

  const startDate = new Date(currentJob.startDate)
  startDate.setHours(0, 0, 0, 0)

  if (startDate > todayEnd)
    return false

  if (!currentJob.endDate)
    return true

  const endDate = new Date(currentJob.endDate)
  endDate.setHours(23, 59, 59, 999)

  return endDate >= todayStart
}

export const promoteResource = (
  resource: IResource,
  job: IResource,
  date: string,
) => {
  const currentJob = getCurrentJob(resource)
  const newJob = {
    ...currentJob,
    positionId: job._id,
    position: job,
    startDate: new Date(date)
  }
  newJob.startDate.setHours(0, 0, 0, 0);
  newJob.endDate = null;

  currentJob.endDate = new Date(date)
  currentJob.endDate.setHours(23, 59, 0, 0);
  currentJob.endDate.setDate(currentJob.endDate.getDate() - 1);  

  for (let i = 0; i < resource.jobs.length; i++) {
    if (resource.jobs[i]._id == currentJob._id)
      resource.jobs[i].endDate = currentJob.endDate;
  }
  
  resource.jobs.push(newJob)
  resource.currentJob = getCurrentJob(resource);
  resource.jobs.sort(sortByDate('startDate'))
  resource.jobs.reverse()

  return resource
}

/**
 * @deprecated use useInfinityResource instead
 */
export const useInfiniteResource = ({ values = ref([]), limit = ref(15), page = ref(0) }) => {
  // <b-autocomplete-infity class="w-full" @get-more-data="getMoreResources" :list="resourceAutoComplete.list"
  // :selected="project.contributors" @add-selected="addSalesContributor"
  // @remove-selected="removeSalesContributor"
  // :haveMore="resourceAutoComplete.haveMore && !resourceAutoComplete.loading">
  // <template #custom-list="{ selectItem, list }">
  const list = ref([])
  const haveMore = ref(true)
  const loading = ref(false)
  const select = (selected) => {
    const resourceIndex = values.value.findIndex(resource => resource._id === selected._id)
    if (resourceIndex > -1)
      return values.value.splice(resourceIndex, 1)
    values.value.push(selected)
  }
  const getMoreData = async (options) => {
    if (options.new) {
      page.value = 0
      haveMore.value = true
    }
    loading.value = true
    const resourceRequest = await resourceApi.filter({ page: page.value, limit: limit.value, display: options.search }).promise
    loading.value = false
    haveMore.value = resourceRequest.count.currentPage < resourceRequest.count.totalPages
    list.value = options.new ? resourceRequest.docs : [...list.value, ...resourceRequest.docs]
    page.value++
    return list
  }

  const remove = (toRemove) => {
    const foundIndex = values.value.findIndex(resource => resource._id === toRemove._id)
    if (foundIndex === -1)
      return

    values.value.splice(foundIndex, 1)
  }

  return {
    haveMore,
    getMoreData,
    remove,
    select,
    list,
    values,
  }
}

/*
export const useInfiniteBoardingProcess = ({ search = ref(''), limit = ref(20), page = ref(0), isLoading = ref(false), list = ref([]) }) => {
  const haveMore = ref(true)
  const getMoreData = async () => {
    try {
      isLoading.value = true

      const searchRequest = boardingProcessApi.filter({ search: search.value, limit: limit.value, page: page.value })
      const searchResponse = await searchRequest.promise
      haveMore.value = searchResponse.count.hasNextPage

      await nextTick() // to be sure have more is updated so the request won't be done again

      list.value = page.value === 0 ? searchResponse.docs : [...list.value, ...searchResponse.docs]

      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false
    }
  }

  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}

*/

// interface IOptions {
//   project: string[] // example : project: ["phonenumber"] => means show the phone number of the resource
// }

/**
 * Hook to manage infinite scrolling of resources.
 * 
 * @param {Object} params - Parameters for the hook.
 * @param {Ref<string>} params.search - Search query.
 * @param {Ref<number>} params.limit - Number of items per page.
 * @param {Ref<number>} params.page - Current page number.
 * @param {Ref<boolean>} params.isLoading - Loading state.
 * @param {Ref<Array>} params.list - List of resources.
 * @param {Object} params.options - Additional options for the request.
 * @returns {Object} - Returns an object containing the state and the getMoreData function.
 */
export const useInfinityResource = ({ search = ref(''), countryFilter = ref(''), limit = ref(20), page = ref(0), isLoading = ref(false), list = ref([]), options = {} as {project?: any, onlyRecruiter?: boolean, firmId?: string, ticketing?: boolean, evaluationShare?: boolean, evaluatorSearch?: boolean, includeFutureJobs?: boolean} }) => {
  const haveMore = ref(true)
  const getMoreData = async () => {
    try {
      isLoading.value = true;
      const { project = null, onlyRecruiter = false, firmId = null, ticketing = false, evaluationShare = false, evaluatorSearch = false, includeFutureJobs = false } = options;
      const queryToSend: any = { display: search.value, limit: limit.value, page: page.value, project }
      if(onlyRecruiter) queryToSend.onlyRecruiter = true
      if(ticketing) queryToSend.ticketing = true
      if(evaluationShare) queryToSend.evaluationShare = true
      if(firmId) queryToSend.firmId = firmId
      if(evaluatorSearch) queryToSend.evaluatorSearch = true
      if(includeFutureJobs) queryToSend.includeFutureJobs = true
      if(countryFilter.value) queryToSend.country = countryFilter.value
      const searchRequest = resourceApi.filter(queryToSend)
      const searchResponse = await searchRequest.promise
      haveMore.value = searchResponse.count.hasNextPage

      await nextTick() // to be sure have more is updated so the request won't be done again

      list.value = page.value === 0 ? searchResponse.docs : [...list.value, ...searchResponse.docs]

      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false;
    }
  }
  
  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}

export const initValues = (newResource, { updateDates = true } = { updateDates: true }) => {

  return newResource;
};

/**
 * Enum for recruitment scope values
 */
export const RecruitmentScope = {
  GLOBAL: 'GLOBAL',
  COUNTRY: 'COUNTRY',
  OFFICEGROUP: 'OFFICEGROUP',
  OFFICE: 'OFFICE',
  MANAGED: 'MANAGED',
} as const

export type RecruitmentScopeType = typeof RecruitmentScope[keyof typeof RecruitmentScope]

export interface IRecruitmentProcess {
  _id: string
  countryId?: string | null
  officeId?: string | { _id: string } | null
  managedBy?: { _id: string }[] | string[]
  [key: string]: any
}

export interface IConnectedResource {
  _id: string
  countryId?: string
  officeId?: string
  profile: {
    modules: {
      recruitment?: {
        recruitmentOverview?: RecruitmentScopeType
        recruitmentOverviewOfficeZone?: { _id: string }[]
        recruitmentOverviewOfficeZoneIds?: string[]
      }
    }
  }
}

/**
 * Filter recruitment processes based on the connected user's recruitment scope
 * @param connectedResource - The connected user's resource data
 * @param processes - List of recruitment processes to filter
 * @returns Object with inScope and outOfScope process arrays
 */
export const filterRecruitmentProcessesByScope = (
  connectedResource: IConnectedResource,
  processes: IRecruitmentProcess[]
): { inScope: IRecruitmentProcess[], outOfScope: IRecruitmentProcess[] } => {
  const recruitmentModule = connectedResource.profile?.modules?.recruitment
  const scope = recruitmentModule?.recruitmentOverview || RecruitmentScope.MANAGED

  const inScope: IRecruitmentProcess[] = []
  const outOfScope: IRecruitmentProcess[] = []

  for (const process of processes) {
    const isInScope = isProcessInScope(connectedResource, process, scope, recruitmentModule)
    if (isInScope) {
      inScope.push(process)
    } else {
      outOfScope.push(process)
    }
  }

  return { inScope, outOfScope }
}

/**
 * Check if a single process is within the user's scope
 */
const isProcessInScope = (
  connectedResource: IConnectedResource,
  process: IRecruitmentProcess,
  scope: RecruitmentScopeType,
  recruitmentModule?: IConnectedResource['profile']['modules']['recruitment']
): boolean => {
  console.log('Checking process', process.countryId, connectedResource.currentJob.office.countryId)
  switch (scope) {
    case RecruitmentScope.GLOBAL:
      return true

    case RecruitmentScope.COUNTRY:
      return process.countryId === connectedResource.currentJob.office.countryId

    case RecruitmentScope.OFFICEGROUP: {
      const officeZoneIds = recruitmentModule?.recruitmentOverviewOfficeZoneIds 
        || recruitmentModule?.recruitmentOverviewOfficeZone?.map(o => o._id) 
        || []
      const processOfficeId = typeof process.officeId === 'object' && process.officeId !== null
        ? process.officeId._id
        : process.officeId
      return officeZoneIds.includes(processOfficeId as string)
    }

    case RecruitmentScope.OFFICE: {
      const processOfficeId = typeof process.officeId === 'object' && process.officeId !== null
        ? process.officeId._id
        : process.officeId
      return processOfficeId === connectedResource.currentJob.office._id
    }

    case RecruitmentScope.MANAGED: {
      const managedByIds = (process.managedBy || []).map(m => 
        typeof m === 'object' && m !== null ? m._id : m
      )
      return managedByIds.includes(connectedResource._id)
    }

    default:
      return false
  }
}

/**
 * based on the job property isStartDate
 * @param resource 
 * @returns 
 */
export const mostRecentJobContract = (resource: IResource) => {
  if(resource.jobs.length === 0) return null
  
  const sortedJobs = resource.jobs.toSorted((a, b) => {
    const dateA = a.endDate ? new Date(a.endDate).getTime() : Date.now()
    const dateB = b.endDate ? new Date(b.endDate).getTime() : Date.now()
    return dateB - dateA
  })

  return sortedJobs.find(job => job.isStartDate) || sortedJobs[sortedJobs.length -1]

}
